import { useParams, useNavigate, useLocation } from "react-router";
import React, { useState, useEffect } from "react";
import CheckoutForm from "../../components/checkout/CheckoutForm";
import PageMeta from "../../components/common/PageMeta";
import CardSummary from "../../components/common/CardSummary";
import publicApi from "../../services/publicApi";
import { useTheme } from "../../context/ThemeContext";
import { CheckCircle, XCircle } from "lucide-react";
import { initializeAllPixels } from '../../utils/firePixel';
import { I18nProvider } from '../../i18n/i18nContext';
import { initializeClarity } from '../../utils/clarity';
import BannerImage from "../../components/checkout/BannerTop";
import { firePageView, trackCheckoutOpened } from '../../utils/pixelTracking';
import CheckoutRenderer from "../../components/checkout/CheckoutRenderer";
import { CheckoutBuilderData } from "../../types/checkoutConfig";


// Função para pegar todos os parâmetros da URL
// Decodifica um pedaço da query string sem nunca lançar erro: "+" vira espaço
// (padrão de formulário/UTMs) e um "%" malformado mantém o texto original em vez
// de derrubar a página do checkout.
function safeDecode(part: string): string {
    const withSpaces = part.replace(/\+/g, ' ');
    try {
        return decodeURIComponent(withSpaces);
    } catch {
        return withSpaces;
    }
}

function getAllUrlParams(url?: string): Record<string, string | boolean | string[]> {
    let queryString = url ? url.split('?')[1] : window.location.search.slice(1);
    const obj: Record<string, string | boolean | string[]> = {};
    if (queryString) {
        queryString = queryString.split('#')[0];
        const arr = queryString.split('&');
        for (let i = 0; i < arr.length; i++) {
            if (!arr[i]) continue;
            // Divide só no primeiro "=" (valores podem conter "=")
            const eq = arr[i].indexOf('=');
            const rawName = eq === -1 ? arr[i] : arr[i].slice(0, eq);
            const rawValue = eq === -1 ? undefined : arr[i].slice(eq + 1);
            const paramName = safeDecode(rawName);
            if (!paramName) continue;
            const paramValue: string | boolean = typeof rawValue === 'undefined' ? true : safeDecode(rawValue);
            if (obj[paramName]) {
                if (typeof obj[paramName] === 'string') {
                    obj[paramName] = [obj[paramName] as string];
                }
                (obj[paramName] as string[]).push(paramValue as string);
            } else {
                obj[paramName] = paramValue;
            }
        }
    }
    return obj;
}

const Checkout = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [utmParams, setUtmParams] = useState({});
    const [checkoutLanguage, setCheckoutLanguage] = useState<string>('pt');


    useEffect(() => {
        document.documentElement.classList.remove("dark");

        // Inicializa o Microsoft Clarity
        initializeClarity();

        return () => {
            if (theme === "dark") {
                document.documentElement.classList.add("dark");
            }
        };
    }, [theme]);

    useEffect(() => {
        const params = getAllUrlParams(location.search);

        if (Object.keys(params).length > 0) {
            setUtmParams(params);
            localStorage.setItem("sellyo_utm_params", JSON.stringify(params));
        } else {
            try {
                const stored = localStorage.getItem("sellyo_utm_params");
                if (stored) setUtmParams(JSON.parse(stored));
            } catch {
                localStorage.removeItem("sellyo_utm_params");
            }
        }
    }, [location.search]);

    const [loading, setLoading] = useState<boolean>(true);
    const [isOfferAvailable, setIsOfferAvailable] = useState<boolean>(true);
    const [isCompanyAvailable, setIsCompanyAvailable] = useState<boolean>(true);
    const [clientIp, setClientIp] = useState<string>("");
    const [sessionId, setSessionId] = useState<string>("");

    // Estados para o overlay de processamento de pagamento
    const [showPaymentOverlay, setShowPaymentOverlay] = useState<boolean>(false);
    const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'failure'>('processing');
    const [paymentErrorMessage, setPaymentErrorMessage] = useState<string>("");

    // Função para obter o IP real do cliente
    async function getClientIP() {
        try {
            // Tenta múltiplos serviços para garantir que obteremos o IP
            const services = [
                'https://api.ipify.org?format=json',
                'https://ipinfo.io/json',
                'https://api.ip.sb/jsonip'
            ];

            // Tenta cada serviço até obter sucesso
            for (const service of services) {
                try {
                    const response = await fetch(service);
                    const data = await response.json();
                    // Diferentes APIs retornam o IP em campos diferentes
                    const ip = data.ip || data.query;

                    if (ip) {
                        return ip;
                    }
                } catch (e) {
                    console.error(`Erro ao obter IP de ${service}:`, e);
                    // Continua para o próximo serviço
                }
            }

            throw new Error('Não foi possível obter o IP do cliente de nenhum serviço');
        } catch (error) {
            console.error('Erro ao obter IP do cliente:', error);
            return null;
        }
    }

    // Effect para obter o IP do cliente
    useEffect(() => {
        const fetchClientIP = async () => {
            // Primeiro verifica se já temos um IP armazenado no localStorage
            const storedIp = localStorage.getItem('spip');

            if (storedIp) {
                setClientIp(storedIp);
            } else {
                // Se não temos um IP armazenado, obtém um novo
                const newIp = await getClientIP();
                if (newIp) {
                    localStorage.setItem('spip', newIp);
                    setClientIp(newIp);
                }
            }
        };

        fetchClientIP();

        // Define um intervalo para atualizar o IP a cada 24 horas
        const intervalId = setInterval(async () => {
            const newIp = await getClientIP();
            if (newIp) {
                localStorage.setItem('spip', newIp);
                setClientIp(newIp);
            }
        }, 24 * 60 * 60 * 1000); // 24 horas

        return () => clearInterval(intervalId);
    }, []);

    const [productName, setProductName] = useState<string>(() => {
        const savedName = localStorage.getItem('ckpname');
        return savedName || "Pagamento Seguro";
    });

    useEffect(() => {
        if (productName && productName !== "Pagamento Seguro") {
            localStorage.setItem('ckpname', productName);
        }
    }, [productName]);

    const handleInstallmentChange = (
        installment: string,
        value: number,
        isFirst: boolean,
        total: number
    ) => {
        // ROBUSTEZ: Validações para evitar valores inválidos
        if (!installment || value <= 0 || total <= 0) {
            console.warn('Valores de parcela inválidos recebidos:', { installment, value, total });
            return;
        }

        setSelectedInstallment(installment);
        setInstallmentValue(value);
        setIsFirstInstallment(isFirst);
        setTotalValue(total);
    };

    // Só os setters são usados (o CheckoutForm informa a parcela escolhida)
    const [, setSelectedInstallment] = useState<string>("1");
    const [installmentValue, setInstallmentValue] = useState<number>(0);
    const [, setIsFirstInstallment] = useState<boolean>(true);
    const [totalValue, setTotalValue] = useState<number>(0);

    // Estados relacionados a cupom e parcelas
    const [appliedCouponData, setAppliedCouponData] = useState<any>(null);
    const [couponDiscount, setCouponDiscount] = useState<number>(0);
    const [discountedInstallmentOptions, setDiscountedInstallmentOptions] = useState<any[]>([]);
    const [selectedOrderBumps, setSelectedOrderBumps] = useState<string[]>([]);
    const [offerData, setOfferData] = useState<any>(null);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    // Detectar se é mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Estilos globais do checkout vindos do visualConfig (background, imagem etc.)
    const desktopGlobalSettings = offerData?.checkout?.visualConfig?.desktop?.globalSettings;
    const mobileGlobalSettings = offerData?.checkout?.visualConfig?.mobile?.globalSettings;
    const effectiveGlobalSettings = isMobile
        ? (mobileGlobalSettings || desktopGlobalSettings)
        : (desktopGlobalSettings || mobileGlobalSettings);

    const checkoutBackgroundStyle: React.CSSProperties = {
        backgroundColor: '#fff', // fallback padrão quando não há configuração
    };

    if (effectiveGlobalSettings) {
        if (effectiveGlobalSettings.backgroundColor) {
            checkoutBackgroundStyle.backgroundColor = effectiveGlobalSettings.backgroundColor;
        }
        if (effectiveGlobalSettings.backgroundImage) {
            checkoutBackgroundStyle.backgroundImage = `url(${effectiveGlobalSettings.backgroundImage})`;
            checkoutBackgroundStyle.backgroundAttachment = effectiveGlobalSettings.fixedBackground ? 'fixed' : 'scroll';
            checkoutBackgroundStyle.backgroundRepeat = effectiveGlobalSettings.repeatBackground ? 'repeat' : 'no-repeat';
            checkoutBackgroundStyle.backgroundSize = effectiveGlobalSettings.expandBackground ? 'cover' : 'auto';
        }
    }

    // Garantir que o fundo do body acompanhe o fundo do checkout,
    // evitando qualquer faixa branca no topo/laterais.
    useEffect(() => {
        const prevBodyBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = checkoutBackgroundStyle.backgroundColor as string;
        return () => {
            document.body.style.backgroundColor = prevBodyBg;
        };
    }, [checkoutBackgroundStyle.backgroundColor]);

    const { id: offerId } = useParams<{ id: string }>();

    useEffect(() => {
        if (offerId) {
            const sessionKey = `sessionId_${offerId}`;
            const checkOfferStatus = async () => {
                try {
                    const response = await publicApi.get(`/api/offers/${offerId}`);
                    const data = response.data;

                    // CORREÇÃO: Chamar /api/checkout/calculate para obter opções de parcelamento corretas
                    try {
                        const calculateResponse = await publicApi.post('/api/checkout/calculate', {
                            offerId: offerId,
                            selectedBumps: [], // Inicialmente sem order bumps
                            couponCode: '', // Inicialmente sem cupom
                            installments: 1 // Começa com 1x
                        });

                        if (calculateResponse.data.success && calculateResponse.data.updatedInstallments) {
                            // Atualizar installmentOptions com os valores calculados pelo backend
                            data.installmentOptions = calculateResponse.data.updatedInstallments;
                        }
                    } catch (calcError) {
                        console.error('Erro ao calcular parcelamento inicial:', calcError);
                        // Continua com os dados originais se houver erro
                    }

                    setOfferData(data);

                    // Define o idioma do checkout com base na propriedade checkoutLanguage da oferta
                    const lang = data.checkoutLanguage || 'pt'; // Usa 'pt' como fallback se não tiver idioma definido

                    // Define o idioma do checkout com base na propriedade checkoutLanguage da oferta
                    setCheckoutLanguage(lang);

                    // --- Disparo dos pixels (sempre dispara, incluindo pixel da Sellyo)
                    const pixels = data?.product?.pixels || [];

                    // Inicializa os pixels (o da Sellyo entra automaticamente), dispara PageView e
                    // InitiateCheckout ao abrir o checkout — navegador + API de Conversões, mesmo event_id
                    initializeAllPixels(pixels).then(() => {
                        const sid = localStorage.getItem(sessionKey) || data.sessionId;
                        firePageView(pixels, sid);
                        if (sid) {
                            trackCheckoutOpened(pixels, {
                                offerId: data.id,
                                productId: data.product?.id,
                                value: Number(data.price) || 0,
                                currency: data.currency || 'BRL',
                                sessionId: sid,
                            });
                        }
                    });

                    // Gerenciar sessionId exclusivo para cada offerId
                    const sessionId = localStorage.getItem(sessionKey);

                    if (!sessionId && data.sessionId) {
                        localStorage.setItem(sessionKey, data.sessionId);
                        setSessionId(data.sessionId);
                    } else if (sessionId) {
                        setSessionId(sessionId);
                    }

                    let productNameFromApi = "Pagamento Seguro";
                    if (data && data.product && data.product.name) {
                        productNameFromApi = data.product.name;
                    }

                    if (productNameFromApi !== "Pagamento Seguro") {
                        localStorage.setItem('ckpname', productNameFromApi);
                    }

                    setProductName(productNameFromApi);

                    const isCompanyActive = data.product?.company?.status === "active";
                    const isOfferActive = data.active === true;
                    const isProductActive = data.product?.status === "active";
                    const hasActivePaymentLink = data.paymentLink?.some(
                        (link: any) => link.status === "active"
                    );

                    const isAvailable = isOfferActive && isProductActive && hasActivePaymentLink;

                    setIsOfferAvailable(isAvailable);
                    setIsCompanyAvailable(isCompanyActive);

                } catch (err: any) {
                    console.error("Erro ao verificar status da oferta:", err);
                    setIsOfferAvailable(false);
                    setIsCompanyAvailable(false);
                } finally {
                    setLoading(false);
                }
            };

            checkOfferStatus();
        }
    }, [offerId]);

    // O CheckoutForm é responsável por toda a lógica de parcelas


    const getPageTitle = () => {
        const savedName = localStorage.getItem('ckpname');
        if (savedName && savedName !== "Pagamento Seguro") {
            return savedName;
        }

        if (productName && productName !== "Pagamento Seguro") {
            return productName;
        }

        return "Pagamento Seguro";
    };

    const getProductImage = () => {
        // SEMPRE retorna a imagem do produto (não o banner)
        const productImage = offerData?.product?.image;
        return productImage || null;
    };

    const getPageDescription = () => {
        // Formato: Nome da Empresa
        const companyName = offerData?.product?.company?.companyName || offerData?.product?.company?.name;
        return companyName || "Sellyo | Pagamento Seguro";
    };

    // Função para atualizar os order bumps selecionados
    const handleOrderBumpsChange = (bumps: string[]) => {
        setSelectedOrderBumps(bumps);
    };

    // Função para receber dados do cupom aplicado
    const handleCouponApplied = (discount: number, couponData: any, installmentOptions?: any[]) => {
        setCouponDiscount(discount);
        setAppliedCouponData(couponData);
        if (installmentOptions) {
            setDiscountedInstallmentOptions(installmentOptions);
        }
    };

    // Handlers para o processamento de pagamento
    const handlePaymentSuccess = (response: any) => {
        setShowPaymentOverlay(true);
        setPaymentStatus('success');

        setTimeout(() => {
            // Limpa o sessionId do localStorage
            localStorage.removeItem(`sessionId_${offerId}`);

            // Limpa UTMs do localStorage
            localStorage.removeItem("sellyo_utm_params");

            // Url de redirecionamento vinda da api
            const redirectUrl = response.data?.redirectUrl;
            if (redirectUrl) {
                window.location.href = redirectUrl;
            } else {
                // Se não houver redirectUrl, tenta redirecionar usando orderId retornado
                const orderId = response.data?.orderId;
                if (orderId) {
                    navigate(`/success/${orderId}`);
                } else {
                    // Fallback derradeiro (quase impossível de acontecer)
                    navigate('/success');
                }
            }
        }, 1000);
    };

    const handlePaymentError = (error: any) => {
        setShowPaymentOverlay(true);
        setPaymentStatus('failure');

        // Extrair a mensagem de erro
        let errorMessage = "Ocorreu um erro ao processar o pagamento.";

        // Verificar se temos um erro com formato específico da API
        if (error.response && error.response.data) {
            const responseData = error.response.data;

            // Verificar se temos o formato de erro com array de errors
            if (responseData.errors && responseData.errors.length > 0) {
                // Pegar a descrição do primeiro erro
                if (responseData.errors[0].description) {
                    errorMessage = responseData.errors[0].description;
                } else if (responseData.errors[0].message) {
                    errorMessage = responseData.errors[0].message;
                }
            }
            // Verificar se temos uma mensagem direta
            else if (responseData.message) {
                errorMessage = responseData.message;
            }
            // Verificar se temos uma descrição direta
            else if (responseData.description) {
                errorMessage = responseData.description;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }
        setPaymentErrorMessage(errorMessage);
    };

    const handleRetryPayment = () => {
        setShowPaymentOverlay(false);
        setPaymentStatus('processing');
        setPaymentErrorMessage("");
    };

    // Função para mostrar o overlay de processamento
    const handlePaymentProcessing = () => {
        setShowPaymentOverlay(true);
        setPaymentStatus('processing');
    };

    const OfferUnavailable = () => (
        <CardSummary className="w-full max-w-lg mx-auto">
            <div className="flex flex-col items-center justify-center py-16 px-8">
                <div className="w-48 h-48 mb-4 flex items-center justify-center">
                    <img src="/images/payment/off.svg" alt="Oferta Encerrada" className="w-full h-full" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3">Vendas Encerradas</h2>
                <p className="text-gray-600 text-center text-lg max-w-sm">
                    Infelizmente as vendas para esta oferta não estão mais disponíveis.
                </p>
            </div>
        </CardSummary>
    );

    const CompanyUnavailable = () => (
        <CardSummary className="w-full max-w-lg mx-auto">
            <div className="flex flex-col items-center justify-center py-16 px-8">
                <div className="w-48 h-48 mb-4 flex items-center justify-center">
                    <img src="/images/payment/off.svg" alt="Oferta Encerrada" className="w-full h-full" />
                </div>
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-3">Vendas Encerradas</h2>
                <p className="text-gray-600 text-center text-lg max-w-sm">
                    Infelizmente as vendas para esta empresa não estão mais disponíveis.
                </p>
            </div>
        </CardSummary>
    );

    // Componente de Overlay para processamento de pagamento
    const PaymentProcessingOverlay = () => (
        <div className="fixed inset-0 z-[999999] overflow-hidden flex items-center justify-center bg-black/70">
            <div className="bg-white rounded-lg ml-6 mr-6 px-8 py-8 max-w-md w-full shadow-lg text-center">
                {paymentStatus === 'processing' && (
                    <>
                        <div className="flex justify-center mb-4">
                            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Processando pagamento...</h3>
                        <p className="text-gray-600">Por favor, aguarde enquanto seu pagamento é processado com segurança.</p>
                    </>
                )}

                {paymentStatus === 'success' && (
                    <>
                        <div className="flex justify-center mb-4">
                            <CheckCircle className="h-16 w-16 text-green-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pagamento Aprovado!</h3>
                        <p className="text-gray-600">Seu pagamento foi processado com sucesso. Aguarde...</p>
                    </>
                )}

                {paymentStatus === 'failure' && (
                    <>
                        <div className="flex justify-center mb-4">
                            <XCircle className="h-16 w-16 text-red-500" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Pagamento Recusado!</h3>
                        <p className="text-gray-600 mb-4">{paymentErrorMessage}</p>
                        <button
                            onClick={handleRetryPayment}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Tentar Novamente
                        </button>
                    </>
                )}
            </div>
        </div>
    );

    // Verifica se temos todas as informações necessárias para renderizar
    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    // Lateral do checkout ativa (layout de duas colunas com a linha lateral criada no builder)
    const hasSideColumn = !isMobile
        && offerData?.checkout?.visualConfig?.desktop?.globalSettings?.checkoutLayout === 'two-columns'
        && !!offerData?.checkout?.visualConfig?.desktop?.document?.nodes?.['side-area-row'];

    return (
        <I18nProvider initialLanguage={checkoutLanguage}>
            <>
                <div className="min-h-screen" style={checkoutBackgroundStyle}>
                    <PageMeta
                        title={getPageTitle()}
                        description={getPageDescription()}
                        image={getProductImage() || undefined}
                        url={window.location.href}
                    />
                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : !isOfferAvailable ? (
                        <div className="flex justify-center items-center min-h-[calc(100vh-100px)] py-16">
                            <OfferUnavailable />
                        </div>
                    ) : !isCompanyAvailable ? (
                        <div className="flex justify-center items-center min-h-[calc(100vh-100px)] py-16">
                            <CompanyUnavailable />
                        </div>
                    ) : (
                        <>
                            {/* Verificar se existe visualConfig com desktop/mobile */}
                            {offerData?.checkout?.visualConfig?.desktop || offerData?.checkout?.visualConfig?.mobile ? (
                                // NOVO FORMATO: Renderizar usando CheckoutBuilder
                                <div className="container mx-auto mt-3">
                                    {/* Componentes do topo - desktop */}
                                    <div className="w-full justify-center mb-4 px-4 hidden lg:flex">
                                        <div className="w-full max-w-5xl">
                                            <CheckoutRenderer
                                                builderConfig={{
                                                    configurations: {
                                                        desktop: offerData.checkout.visualConfig.desktop || { document: { root: [], nodes: {} }, globalSettings: {} },
                                                        mobile: offerData.checkout.visualConfig.mobile || { document: { mobileRoot: [], nodes: {} }, globalSettings: {} }
                                                    }
                                                } as CheckoutBuilderData}
                                                isMobile={false}
                                            />
                                        </div>
                                    </div>

                                    {/* Componentes do topo - mobile */}
                                    <div className="w-full flex justify-center mb-4 px-3 lg:hidden">
                                        <div className="w-full max-w-6xl">
                                            <CheckoutRenderer
                                                builderConfig={{
                                                    configurations: {
                                                        desktop: offerData.checkout.visualConfig.desktop || { document: { root: [], nodes: {} }, globalSettings: {} },
                                                        mobile: offerData.checkout.visualConfig.mobile || { document: { mobileRoot: [], nodes: {} }, globalSettings: {} }
                                                    }
                                                } as CheckoutBuilderData}
                                                isMobile={true}
                                            />
                                        </div>
                                    </div>

                                    {/* Layout flex para formulário + lateral */}
                                    <div className={`flex flex-col items-center lg:flex-row lg:items-start lg:justify-center gap-4 ${hasSideColumn ? 'lg:w-full lg:max-w-[66rem] lg:mx-auto lg:px-4' : ''}`}>
                                        {/* Coluna principal: Formulário (com lateral, ocupa o resto da largura do topo) */}
                                        <div className={`w-full max-w-3xl lg:mx-0 ${hasSideColumn ? 'lg:flex-1 lg:min-w-0 lg:-ml-3' : ''}`}>
                                            <CheckoutForm
                                                offerData={offerData}
                                                offerId={offerId}
                                                onSuccess={handlePaymentSuccess}
                                                onError={handlePaymentError}
                                                onInstallmentChange={handleInstallmentChange}
                                                installmentValue={installmentValue}
                                                totalValue={totalValue}
                                                clientIp={clientIp}
                                                onPaymentProcessing={handlePaymentProcessing}
                                                couponDiscount={couponDiscount}
                                                discountedInstallmentOptions={discountedInstallmentOptions}
                                                appliedCouponData={appliedCouponData}
                                                selectedOrderBumps={selectedOrderBumps}
                                                onOrderBumpsChange={handleOrderBumpsChange}
                                                onCouponApplied={handleCouponApplied}
                                                sessionId={sessionId}
                                                utmParams={utmParams}
                                            />
                                        </div>

                                        {/* Coluna lateral: Componentes da lateral */}
                                        {hasSideColumn && (
                                            <div className="hidden lg:block w-full lg:w-72 lg:shrink-0">
                                                <div className="sticky top-6">
                                                    <CheckoutRenderer
                                                        builderConfig={{
                                                            configurations: {
                                                                desktop: offerData.checkout.visualConfig.desktop,
                                                                mobile: { document: { mobileRoot: [], nodes: {} }, globalSettings: {} }
                                                            }
                                                        }}
                                                        isMobile={false}
                                                        renderOnlyRows={['side-area-row']}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Mobile footer: renderizar mobile-footer-row abaixo do formulário */}
                                    {offerData.checkout.visualConfig.mobile?.document?.nodes?.['mobile-footer-row'] && (
                                        <div className="mt-4 pb-8 w-full flex justify-center px-4 lg:hidden">
                                            <div className="w-full max-w-3xl">
                                                <CheckoutRenderer
                                                    builderConfig={{
                                                        configurations: {
                                                            desktop: offerData.checkout.visualConfig.desktop || { document: { root: [], nodes: {} }, globalSettings: {} },
                                                            mobile: offerData.checkout.visualConfig.mobile,
                                                        },
                                                    } as CheckoutBuilderData}
                                                    isMobile={true}
                                                    renderOnlyRows={["mobile-footer-row"]}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                // FORMATO ANTIGO: Manter compatibilidade com bannerTop
                                <>
                                    {/* Container externo para centralizar o banner com mesma largura do form */}
                                    <div className="container mx-auto mt-3 flex justify-center">
                                        <div className="w-full max-w-3xl">
                                            <BannerImage
                                                imageUrl={offerData?.checkout?.[0]?.visualConfig?.bannerTop || null}
                                            />
                                        </div>
                                    </div>

                                    <div className="container mx-auto mt-3">
                                        {/* Layout flex principal que organiza o conteúdo */}
                                        <div className="flex flex-col items-center lg:flex-row lg:items-start lg:justify-center">
                                            {/* Container principal do formulário - centralizado quando não há banner lateral */}
                                            <div className="w-full max-w-3xl">
                                                <CheckoutForm
                                                    offerData={offerData}
                                                    offerId={offerId}
                                                    onSuccess={handlePaymentSuccess}
                                                    onError={handlePaymentError}
                                                    onInstallmentChange={handleInstallmentChange}
                                                    installmentValue={installmentValue}
                                                    totalValue={totalValue}
                                                    clientIp={clientIp}
                                                    onPaymentProcessing={handlePaymentProcessing}
                                                    couponDiscount={couponDiscount}
                                                    discountedInstallmentOptions={discountedInstallmentOptions}
                                                    appliedCouponData={appliedCouponData}
                                                    selectedOrderBumps={selectedOrderBumps}
                                                    onOrderBumpsChange={handleOrderBumpsChange}
                                                    onCouponApplied={handleCouponApplied}
                                                    sessionId={sessionId}
                                                    utmParams={utmParams}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Overlay de processamento de pagamento */}
                    {showPaymentOverlay && <PaymentProcessingOverlay />}
                </div>
            </>
        </I18nProvider>
    );
};

export default Checkout;